import {
  FeaturedTestimonialCardProps,
  GetTestimonialResponse,
  TestimonialsSectionProps,
} from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import testimonialBgPattern from "assets/image/testimonial-background-pattern.png";
import {
  ArrowUpRightIcon,
  StarEmptyIcon,
  StarHalfIcon,
  StarHalfSolidIcon,
  StarSolidIcon,
} from "assets/icons";
import { getInitials } from "utils/formatter";
import { useState } from "react";
import SideDrawerWrapper from "./SideDrawerWrapper";
import closeIcon from "assets/svg/close-icon.svg";

// Testimonials Component
export default function TestimonialsSection({
  testimonials,
}: TestimonialsSectionProps) {
  const [seeAllReviews, setSeeAllReviews] = useState(false);

  return (
    <section className="mt-[40px] sm:mt-[64px]">
      <div className="w-full flex justify-between items-center">
        <h2 className="text-lg sm:text-xl font-monaMedium text-[#333333]">
          Testimonials
        </h2>
        {testimonials.length > 2 && (
          <button
            onClick={() => setSeeAllReviews(true)}
            className="text-sm sm:text-base px-3 py-[11px] gap-2 flex items-center rounded-lg bg-[#F2ECE2] text-[#20242A] font-monaMedium hover:bg-primary hover:text-white"
          >
            See all {testimonials.length} testimonials{" "}
            <ArrowUpRightIcon stroke="currentColor" />
          </button>
        )}
      </div>
      {/* main */}
      <div className="w-full flex flex-col md:flex-row gap-5 mt-5">
        {testimonials[0] && (
          <div className="w-full md:w-[50%]">
            <FeaturedTestimonialCard
              feedback={testimonials[0].feedback}
              name={testimonials[0].name}
              imageUrl={testimonials[0].imageUrl}
              rating={testimonials[0].rating}
            />
          </div>
        )}
        {testimonials[1] && (
          <div className="w-full md:w-[50%]">
            <FeaturedTestimonialCard
              feedback={testimonials[1].feedback}
              name={testimonials[1].name}
              imageUrl={testimonials[1].imageUrl}
              rating={testimonials[1].rating}
            />
          </div>
        )}
      </div>

      <SideDrawerWrapper
        openDrawer={seeAllReviews}
        handleCloseDrawer={() => setSeeAllReviews(false)}
      >
        <section className="h-full flex flex-col bg-[#FDF6EE]">
          {/* Header */}
          <div className="flex items-center justify-between py-3 px-5 border-b border-[#D5DBE1] bg-[#F2ECE2]">
            <h3 className="text-[20px] font-monaSemiBold text-[#30313D]">
              All {testimonials.length} testimonials
            </h3>
            <button
              onClick={() => setSeeAllReviews(false)}
              className="cursor-pointer"
            >
              <img src={closeIcon} alt="close" />
            </button>
          </div>

          {/* Reviews List */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {testimonials.map((testimonial, index) => (
              <TestimonialListItem key={index} {...testimonial} />
            ))}
          </div>
        </section>
      </SideDrawerWrapper>
    </section>
  );
}

// Featured Testimonial Card Component
function FeaturedTestimonialCard({
  feedback,
  name,
  imageUrl,
  rating = 0,
}: FeaturedTestimonialCardProps) {
  return (
    <div className="bg-[#853A1B] rounded-[20px] w-full p-5 relative">
      <img
        src={testimonialBgPattern}
        alt="pattern"
        className="absolute top-0 left-0 object-cover"
      />
      <p className="text-[#F6F3EF] font-monaMedium text-2xl">"{feedback}"</p>

      <div className="flex gap-[18.77px] mt-10">
        <Rating rating={rating} />
      </div>

      <div className="flex items-center gap-3 mt-8">
        <TestimonialAvatar
          imageUrl={imageUrl}
          name={name}
          className="w-[42.5px] h-[42.5px] sm:w-12 sm:h-12 rounded-full ring-2 ring-white/20"
          fallbackClassName="bg-white/20"
          initialsClassName="text-white font-semibold text-sm sm:text-base"
        />
        <div>
          <div className="font-semibold text-[#F6F3EF] text-sm sm:text-base">
            {name}
          </div>
          {/* <div className="text-[#ACB1B7] text-xs sm:text-sm">
                {testimonials[0].handle}
              </div> */}
        </div>
      </div>
    </div>
  );
}

function TestimonialAvatar({
  imageUrl,
  name,
  className,
  fallbackClassName,
  initialsClassName,
}: {
  imageUrl?: string | null;
  name: string;
  className: string;
  fallbackClassName: string;
  initialsClassName: string;
}) {
  const testimonialImageUrl = imageUrl?.trim();

  if (testimonialImageUrl) {
    return (
      <img
        src={testimonialImageUrl}
        alt={name}
        className={`${className} object-cover object-center`}
      />
    );
  }

  return (
    <div
      className={`${className} ${fallbackClassName} flex items-center justify-center`}
    >
      <span className={initialsClassName}>{getInitials(name)}</span>
    </div>
  );
}

function Rating({ rating = 0 }: { rating?: number }) {
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.1 && rating % 1 <= 0.9;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    // Render filled stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <StarSolidIcon
          key={`filled-${i}`}
          width={16}
          height={16}
          fill="#ffffff"
        />,
      );
    }

    // Render half star if applicable
    if (hasHalfStar) {
      stars.push(
        <StarHalfSolidIcon
          key="half"
          width={16}
          height={16}
          fillLeft="#ffffff"
          fillRight="#ffffff33"
        />,
      );
    }

    // Render empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <StarEmptyIcon
          key={`empty-${i}`}
          width={16}
          height={16}
          fill="#ffffff33"
        />,
      );
    }

    return stars;
  };

  const hasReviews = rating > 0;

  return (
    // <div className="">
    <div className="flex gap-3 items-center justify-end sm:justify-center">
      {hasReviews ? (
        <div className="flex gap-[18.77px]">{renderStars()}</div>
      ) : (
        <p className="inline-block font-monaMedium text-sm text-[#E3592E]">
          No review yet
        </p>
      )}
    </div>
    // </div>
  );
}

// Testimonial List Item for Drawer
function TestimonialListItem({
  feedback,
  name,
  imageUrl,
  rating = 0,
}: GetTestimonialResponse) {
  return (
    <div className="p-4 bg-[#F2ECE2] rounded-2xl">
      {/* Rating */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-monaMedium text-[#E3592E]">
          {rating.toFixed(1)}
        </span>
        <ListItemRating rating={rating} />
      </div>

      {/* Feedback */}
      <p className="text-[#444D5A] text-base md:text-lg leading-relaxed mb-4 font-monaMedium ">
        "{feedback}"
      </p>

      {/* User Info */}
      <div className="flex items-center gap-3">
        <TestimonialAvatar
          imageUrl={imageUrl}
          name={name}
          className="w-10 h-10 rounded-full"
          fallbackClassName="bg-[#853A1B]"
          initialsClassName="text-white font-semibold text-sm"
        />

        <div>
          <p className="font-monaMedium text-[#333333] text-sm">{name}</p>
        </div>
      </div>
    </div>
  );
}

// Compact Rating for List Item
function ListItemRating({ rating = 0 }: { rating?: number }) {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.1 && rating % 1 <= 0.9;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <StarSolidIcon
        key={`filled-${i}`}
        width={14}
        height={14}
        fill="#E3592E"
      />,
    );
  }

  if (hasHalfStar) {
    stars.push(<StarHalfIcon key="half" width={14} height={14} />);
  }

  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <StarEmptyIcon
        key={`empty-${i}`}
        width={14}
        height={14}
        fill="#E5DFD5"
      />,
    );
  }

  return <div className="flex gap-0.5">{stars}</div>;
}
